import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { DOWNLOAD_URL } from "../../data/content";
import { MagneticButton, scrollToId } from "./primitives";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("http")) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else if (href.startsWith("/")) {
      navigate(`/${lang}${href}`);
    } else {
      const isLanding =
        location.pathname.startsWith(`/${lang}`) ||
        location.pathname === `/${lang}/` ||
        location.pathname === "/";
      if (!isLanding) {
        navigate(`/${lang}/${href}`);
      } else {
        scrollToId(href);
      }
    }
  };

  const goHome = () => {
    setOpen(false);
    const isLanding =
      location.pathname === `/${lang}/` || location.pathname === `/${lang}`;
    if (isLanding) {
      scrollToId("#home");
    } else {
      navigate(`/${lang}/`);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-background/60 backdrop-blur-xl border-b border-white/5" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 h-14 sm:h-16 md:h-[72px] flex items-center justify-between">
        <button
          onClick={goHome}
          data-testid="nav-brand"
          className="flex items-center gap-2 sm:gap-2.5 group"
        >
          <svg viewBox="0 0 1080 1080" className="w-6 h-6 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform duration-300" aria-hidden="true">
            <path transform="matrix(0.52734375, 0, 0, 0.52734375, 0, 0)" d="M270.63 377.35 C317 374.69 329.48 396.32 357.39 429.16 L401.48 481.02 L565.85 673.76 C669.46 582.58 818.06 522.37 954.92 509.41 C1146.82 490.25 1338.31 549.54 1485.82 673.79 C1537.52 611.7 1591.35 549.75 1643.8 488.18 L1692.26 431.31 C1712.07 408.1 1728.09 382.69 1760.16 377.68 C1778.22 374.82 1796.68 379.26 1811.46 390.03 C1825.95 400.75 1835.6 416.77 1838.29 434.59 C1840.62 449.39 1837.98 464.55 1830.79 477.7 C1824.2 489.78 1812.51 502.23 1803.41 512.83 L1771.74 549.95 L1644.32 699.49 L1604.45 746.12 C1598.58 753 1588.49 764.15 1583.57 770.97 L1582.41 772.61 C1657.13 869.77 1697.09 951.05 1724.72 1071.18 C1732.47 1106.26 1736.77 1143.2 1739.18 1179.15 C1740.03 1191.94 1739.9 1210.32 1740.44 1223.71 C1741.2 1224.46 1741.95 1225.22 1742.71 1225.98 C1743.5 1314.86 1743.14 1473.69 1743.16 1555.83 L308.12 1555.81 L308.07 1338.36 C308.07 1305.49 308 1272.44 308.17 1239.56 C308.32 1229.85 310.93 1220.05 311.14 1210.37 C311.83 1179.22 314.12 1148.78 318.46 1117.89 C336.7 991.33 388.77 872.04 469.15 772.6 C450.27 749.03 427.81 723.73 408.06 700.57 L295.16 568.2 L252.54 518.21 C238.04 501.19 219.2 482.83 214.13 460.7 C209.89 442.67 213.12 423.7 223.08 408.09 C234.66 389.94 250.27 381.78 270.63 377.35 Z" fill="#436AE7" />
            <path transform="matrix(0.52734375, 0, 0, 0.52734375, 0, 0)" d="M1263.22 921.45 C1321.98 914.45 1375.28 956.41 1382.27 1015.17 C1389.25 1073.93 1347.27 1127.23 1288.51 1134.2 C1229.77 1141.16 1176.5 1099.2 1169.52 1040.46 C1162.54 981.73 1204.48 928.45 1263.22 921.45 Z" fill="#E5E6FB" />
            <path transform="matrix(0.52734375, 0, 0, 0.52734375, 0, 0)" d="M763.07 921.43 C821.93 914.53 875.21 956.73 881.97 1015.6 C888.73 1074.48 846.4 1127.65 787.51 1134.27 C728.82 1140.86 675.86 1098.71 669.13 1040.04 C662.39 981.36 704.42 928.31 763.07 921.43 Z" fill="#E5E6FB" />
          </svg>
          <span className="font-display font-bold text-sm sm:text-base tracking-tight text-white">
            BERRY MOD APK
          </span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {t.nav.links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <MagneticButton
            testId="nav-cta"
            onClick={() => go(DOWNLOAD_URL)}
            className="hidden sm:inline-flex items-center px-5 py-2.5 bg-primary text-white font-mono-tech text-[11px] uppercase tracking-[0.18em] hover:bg-white hover:text-black transition-colors duration-300"
          >
            {t.nav.cta}
          </MagneticButton>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="md:hidden w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border border-white/10 text-white rounded-lg"
            aria-label="Toggle menu"
          >
            {open ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-background/95 backdrop-blur-xl border-b border-white/5"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {t.nav.links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className="text-left py-3 font-display text-lg text-white/80 hover:text-primary border-b border-white/5 transition-colors"
                >
                  {l.label}
                </button>
              ))}


              <button
                onClick={() => go(DOWNLOAD_URL)}
                data-testid="mobile-cta"
                className="mt-4 py-3.5 bg-primary text-white font-mono-tech text-xs uppercase tracking-[0.18em]"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
